import * as React from 'react'
import * as THREE from 'three'
import * as datGui from 'dat.gui'
import Stats from 'stats.js'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'
import { HDRCubeTextureLoader } from 'three/examples/jsm/loaders/HDRCubeTextureLoader'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader'
import { CubeTexture } from 'three'
import { FiSettings } from 'react-icons/fi'
import { IoIosCloseCircle } from 'react-icons/io'

import packageFile from '../../../package.json'

import Typography from '@material-ui/core/Typography'
import IconButton from '@material-ui/core/IconButton'
import Button from '@material-ui/core/Button'
import Card from '@material-ui/core/Card'
import Paper from '@material-ui/core/Paper'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableContainer from '@material-ui/core/TableContainer'
import TableRow from '@material-ui/core/TableRow'

import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js'
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js'
import { OutlinePass } from 'three/examples/jsm/postprocessing/OutlinePass.js'
import { FXAAShader } from 'three/examples/jsm/shaders/FXAAShader.js'

import { gsap } from 'gsap'

import styles from './stage.module.scss'
import initGeoData from 'designer/geoHandlers/initializeGeoData'

import ModelLoader from './GeoHandlers/ModelLoader'
import Model from 'designer/geoHandlers/Components/Model'
import Part from 'designer/geoHandlers/Components/Part'
import Variant from 'designer/geoHandlers/Components/Variant'
import Section from 'designer/geoHandlers/Components/Section'
import Island from 'designer/geoHandlers/Components/Island'

import SetDefaultLights from './Defaults/Lights'
import setDefaultOrbitCtrl from 'designer/initializers/setDefaultsOrbitCtrl'

import ModeSection from './Modes/Section'
import EditorPopup from './EditorPopup'

import QuiltWrapper from './MaterialEditor/Quilt'
import quiltType from './MaterialEditor/Quilt/quiltType'

import Quilt from 'designer/geoHandlers/Material/quilt'

import {
  DesignerGeoType,
  StagePropsType,
  StageStateType,
  selectionTree,
} from './Types'
import MaterialContainer from './MaterialEditor'

const SidebarToggler = ({
  toggler,
  active,
}: {
  toggler: Function
  active: Boolean
}) => {
  return (
    <IconButton
      size="small"
      className={styles.sidebar_toggle_button}
      onClick={() => toggler()}
    >
      {active ? <IoIosCloseCircle /> : <FiSettings />}
    </IconButton>
  )
}

const Sidebar = ({
  active,
  state,
  selectionTree,
}: {
  active: Boolean
  state: StageStateType
  selectionTree: selectionTree[]
}) => {
  if (active) {
    return (
      <Paper
        /* SideBar Wrapper */
        className={styles.sidebar_wrapper}
        square
      >
        <Card variant="outlined" className={styles.sidebar_card}>
          <TableContainer>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell colSpan={2}>
                    <Typography variant="caption">Technical Details</Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>App Version</TableCell>
                  <TableCell>{packageFile.version}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Pixel Ratio</TableCell>
                  <TableCell>
                    {Math.floor(window.devicePixelRatio * 100) / 100}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>WebGL2 Compatibility</TableCell>
                  <TableCell>
                    {Stage.renderer.capabilities.isWebGL2 ? 'true' : 'false'}
                  </TableCell>
                </TableRow>
                {window.devicePixelRatio > 1 &&
                  !Stage.renderer.capabilities.isWebGL2 && (
                    <TableRow>
                      <TableCell colSpan={2}>
                        <Typography variant="caption">
                          You have a HDP Device but the browser you are using is
                          incompatible. You might not get the best quality. Use
                          Google Chrome if possible.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                {window.devicePixelRatio > 1 &&
                  Stage.renderer.capabilities.isWebGL2 && (
                    <TableRow>
                      <TableCell colSpan={2}>
                        <Typography variant="caption">
                          You have a HDP Device and a compatible Browser. You
                          should have the best render quality.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
        <Card variant="outlined" className={styles.sidebar_card}>
          <TableContainer>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell colSpan={2}>
                    <Typography variant="caption">Model Details</Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell># of Models</TableCell>
                  <TableCell>{state.dataLength}</TableCell>
                </TableRow>
                {selectionTree.map(s => (
                  <TableRow>
                    <TableCell>{s.name}</TableCell>
                    <TableCell>{s.value}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Paper /* SideBar Wrapper */>
    )
  } else {
    return null
  }
}

class Stage extends React.Component<StagePropsType, StageStateType> {
  private static instance: Stage

  state = {
    loaded: false,
    ui: {
      sidebar: false,
    },
    update: 0,
    mounted: false,
    data: undefined,
    dataLength: 0,
    mode: new ModeSection(),
    activePoint: null,
    selectPoint3D: null,
    quilt: undefined,
    trigger: false,
  }

  // Essential elements /////////////////////////////////////////////////////
  public container: React.RefObject<HTMLDivElement>
  public static scene: THREE.Scene
  public static camera: THREE.PerspectiveCamera
  public static renderer: THREE.WebGLRenderer
  public static renderTarget:
    | THREE.WebGLRenderTarget
    | THREE.WebGLMultisampleRenderTarget
  public static canvas: HTMLCanvasElement
  public static orbitCtrl: OrbitControls
  public static envMap: THREE.CubeTexture | undefined

  public static composer: EffectComposer
  public static renderPass: RenderPass
  public static outlinePass: OutlinePass
  public static effectFXAA: ShaderPass

  // Geometry ///////////////////////////////////////////////////////////////
  public geo: DesignerGeoType[]
  public SDPaths: String[]
  public BKPaths: String[]
  public geoSD: THREE.Mesh[]
  public geoBK: THREE.Mesh[]
  public geoSelectable: THREE.Mesh[]

  // Selection //////////////////////////////////////////////////////////////
  public static selectableModels: Model[] = []
  public static selectableParts: Part[] = []
  public static selectableVariants: Variant[] = []
  public static selectableSections: Section[] = []
  public static selectableIslands: Island[] = []
  public static currentSelection:
    | Model[]
    | Part[]
    | Variant[]
    | Section[]
    | Island[] = []

  // Materials //////////////////////////////////////////////////////////////
  public defMatPalette: THREE.MeshStandardMaterial[]
  public defLeatherNormals: THREE.Texture

  // RayCaster //////////////////////////////////////////////////////////////
  public static rayCaster: THREE.Raycaster = new THREE.Raycaster()
  public static mouseCords: THREE.Vector2 = new THREE.Vector2()
  public static camCords: THREE.Vector3 = new THREE.Vector3()

  // Loaders ////////////////////////////////////////////////////////////////
  public loadingManager: THREE.LoadingManager
  public loaderTexture: THREE.TextureLoader
  public loaderCubeTexture: THREE.CubeTextureLoader
  public loaderHDRCubeTexture: HDRCubeTextureLoader
  public loaderGLTFLoader: GLTFLoader

  // Render Helpers /////////////////////////////////////////////////////////
  public pmremGenerator: THREE.PMREMGenerator

  // Debug UI ///////////////////////////////////////////////////////////////
  public static datgui: dat.GUI
  public static stats: Stats | undefined

  private static datguiFolders: dat.GUI[] = []

  private renderDebounceTimer: NodeJS.Timeout | undefined

  // Quilting ///////////////////////////////////////////////////////////////
  private static _quilts: Quilt[] = []

  private static _quiltUid: string[] = []

  // NOTE: property identifiers
  // Thread thickness in milimeter and pixels
  private static _threadThickness: { mm: number; px: number } = {
    mm: 0.3,
    px: 2,
  }
  private static _stitch: { length: number; gap: number } = {
    length: 0.5,
    gap: 0.05 * 2,
  }

  static get threadThickness() {
    return Stage._threadThickness
  }

  static get stitch() {
    return this._stitch
  }

  public static getInstance(): Stage {
    if (!Stage.instance) {
      Stage.instance = new Stage({})
    }
    return Stage.instance
  }

  private constructor(props: any) {
    super(props)

    // Essential elements /////////////////////////////////////////////////////
    this.container = React.createRef()
    Stage.scene = new THREE.Scene()
    const cRatio = window.innerWidth / window.innerHeight
    Stage.camera = new THREE.PerspectiveCamera(55, cRatio, 1, 10)
    Stage.renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true })
    Stage.canvas = Stage.renderer.domElement

    if (window.devicePixelRatio > 1 && Stage.renderer.capabilities.isWebGL2) {
      Stage.renderTarget = new THREE.WebGLMultisampleRenderTarget(800, 600, {
        minFilter: THREE.LinearFilter,
        magFilter: THREE.LinearFilter,
        format: THREE.RGBAFormat,
        encoding: THREE.sRGBEncoding,
      })
      console.debug('Using WebGLMultisampleRenderTarget')
    } else {
      Stage.renderTarget = new THREE.WebGLRenderTarget(800, 600, {
        minFilter: THREE.LinearFilter,
        magFilter: THREE.LinearFilter,
        format: THREE.RGBAFormat,
        encoding: THREE.sRGBEncoding,
      })
      console.debug('Using WebGLRenderTarget')
    }

    Stage.orbitCtrl = new OrbitControls(Stage.camera, Stage.renderer.domElement)
    Stage.camera.position.x = 0
    Stage.camera.position.y = 500
    Stage.camera.position.z = 0
    Stage.camera.lookAt(0, 25, 100)
    Stage.orbitCtrl.update()
    Stage.orbitCtrl.addEventListener('change', this.renderScene)

    // Pass Compositing /////////////////////////////////////////////////////
    Stage.composer = new EffectComposer(Stage.renderer, Stage.renderTarget)

    Stage.renderPass = new RenderPass(Stage.scene, Stage.camera)

    Stage.outlinePass = new OutlinePass(
      new THREE.Vector2(window.innerWidth, window.innerHeight),
      Stage.scene,
      Stage.camera
    )
    Stage.outlinePass.hiddenEdgeColor = new THREE.Color('#222222')
    Stage.outlinePass.selectedObjects = []

    Stage.effectFXAA = new ShaderPass(FXAAShader)
    Stage.effectFXAA.uniforms['resolution'].value.set(
      1 / window.innerWidth,
      1 / window.innerHeight
    )

    Stage.composer.addPass(Stage.renderPass)
    Stage.composer.addPass(Stage.outlinePass)
    Stage.composer.addPass(Stage.effectFXAA)

    // Geometry ///////////////////////////////////////////////////////////////
    this.geo = this.props.geo

    this.SDPaths = []
    this.BKPaths = []
    this.geoSD = []
    this.geoBK = []
    this.geoSelectable = []

    // Loaders ////////////////////////////////////////////////////////////////
    this.loadingManager = new THREE.LoadingManager()
    this.loaderTexture = new THREE.TextureLoader(this.loadingManager)
    this.loaderCubeTexture = new THREE.CubeTextureLoader(this.loadingManager)
    this.loaderHDRCubeTexture = new HDRCubeTextureLoader(this.loadingManager)
    this.loaderGLTFLoader = new GLTFLoader(this.loadingManager)

    // Materials //////////////////////////////////////////////////////////////
    this.defMatPalette = []
    this.defLeatherNormals = this.loaderTexture.load('/material/leather/01.jpg')
    this.defLeatherNormals.wrapS = THREE.RepeatWrapping
    this.defLeatherNormals.wrapT = THREE.RepeatWrapping
    this.defLeatherNormals.repeat.set(0.6, 0.6)

    // Render Helpers /////////////////////////////////////////////////////////
    this.pmremGenerator = new THREE.PMREMGenerator(Stage.renderer)

    // Debug UI ///////////////////////////////////////////////////////////////
    // sometimes there is multiple initializations
    if (!Stage.datgui) {
      Stage.datgui = new datGui.GUI()
    }
    Stage.datgui.hide()

    /* const gridHelper = new THREE.GridHelper(120, 12)
     * Stage.scene.add(gridHelper)
     * const axesHelper = new THREE.AxesHelper(5)
     * axesHelper.position.x = -60
     * axesHelper.position.z = -60
     * Stage.scene.add(axesHelper) */

    Stage.renderer.physicallyCorrectLights = true
    Stage.renderer.outputEncoding = THREE.sRGBEncoding
    Stage.renderer.toneMapping = THREE.ACESFilmicToneMapping
    Stage.renderer.toneMappingExposure = 0.5

    this.resizeCanvas()

    Stage.instance = this
  }

  componentDidMount = () => {
    if (this.container.current) {
      this.container.current.appendChild(Stage.canvas)
      this.resizeCanvas()
      window.addEventListener('resize', this.resizeCanvas)
      this.setState({ mounted: true }, () => {
        initGeoData(this.props.geo)
          .then(data => {
            this.setState({ data, dataLength: data ? data.length : 0 }, () => {
              gsap.to(Stage.camera, {
                far: 1000,
                duration: 1,
                ease: 'power1.out',
                onUpdate: () => {
                  Stage.camera.updateProjectionMatrix()
                },
              })
              gsap.to(Stage.camera.position, {
                x: 60,
                y: 52,
                z: 14,
                duration: 1,
                delay: 0.5,
                ease: 'power2.out',
                onUpdate: () => {
                  Stage.orbitCtrl.update()
                  this.update()
                },
              })
            })
          })
          .then(() => setDefaultOrbitCtrl())
          .catch(err => console.log(err))
      })
      Stage.orbitCtrl.addEventListener('start', this.listnerOrbitStart)
      Stage.orbitCtrl.addEventListener('end', this.listnerOrbitEnd)
      /* Stage.orbitCtrl.addEventListener('change', this.listnerOrbitChange) */
      Stage.canvas.addEventListener('mousemove', this.listnerMouseMove)
      const cameraDebug = Stage.datgui.addFolder('Camera')
      Stage.datguiFolders.push(cameraDebug)
      cameraDebug
        .add(Stage.camera.position, 'x', -200, 200, 1)
        .name('x')
        .onChange(() => {
          Stage.orbitCtrl.update()
          this.update()
        })
      cameraDebug
        .add(Stage.camera.position, 'y', -200, 200, 1)
        .name('y')
        .onChange(() => {
          Stage.orbitCtrl.update()
          this.update()
        })
      cameraDebug
        .add(Stage.camera.position, 'z', -200, 200, 1)
        .name('z')
        .onChange(() => {
          Stage.orbitCtrl.update()
          this.update()
        })
    }
    const debugToneMappingFolder = Stage.datgui.addFolder('Renderer')
    Stage.datguiFolders.push(debugToneMappingFolder)
    const params = {
      toneMapping: 'ACESFilmic',
    }
    interface toneMappingTypes {
      [key: string]: number
    }
    const toneMappingOptions: toneMappingTypes = {
      None: THREE.NoToneMapping,
      Linear: THREE.LinearToneMapping,
      Reinhard: THREE.ReinhardToneMapping,
      Cineon: THREE.CineonToneMapping,
      ACESFilmic: THREE.ACESFilmicToneMapping,
    }
    debugToneMappingFolder
      .add(params, 'toneMapping', Object.keys(toneMappingOptions))
      .onChange((e: string) => {
        Stage.renderer.toneMapping = toneMappingOptions[e]
        Stage.scene.traverse(e => {
          if (e.type === 'Mesh') {
            const mesh = e as THREE.Mesh
            if (Array.isArray(mesh.material)) {
              mesh.material[0].needsUpdate = true
            } else {
              mesh.material.needsUpdate = true
            }
          }
        })
        this.update()
      })
    debugToneMappingFolder
      .add(Stage.renderer, 'toneMappingExposure', 0, 5, 0.1)
      .onChange(e => {
        Stage.renderer.toneMappingExposure = e
        this.update()
      })
    document.addEventListener('keypress', this.statsToggle)
    document.addEventListener('keypress', this.logger)
  }

  componentWillUnmount() {
    for (let folder of Stage.datguiFolders) {
      Stage.datgui.removeFolder(folder)
    }
    Stage.datguiFolders = []
    Stage.orbitCtrl.removeEventListener('start', this.listnerOrbitStart)
    Stage.orbitCtrl.removeEventListener('end', this.listnerOrbitEnd)
    /* Stage.orbitCtrl.removeEventListener('change', this.listnerOrbitChange) */
    Stage.canvas.removeEventListener('mousemove', this.listnerMouseMove)
    Stage.selectableModels = []
    Stage.selectableParts = []
    Stage.selectableVariants = []
    Stage.selectableSections = []
    Stage.selectableIslands = []
    Stage.scene.clear()
  }

  private resizeCanvas = () => {
    // For resizing the Canvas when the container changes size.
    //       w = window
    //       c = container
    if (this.container.current) {
      const wWidth: number = 10
      const wHeight: number = 10
      Stage.renderer.setSize(wWidth, wHeight, false)
      Stage.composer.setSize(wWidth, wHeight)
      const cWidth: number = this.container.current.clientWidth
      const cHeight: number = this.container.current.clientHeight
      const pixelRatio = window.devicePixelRatio
      Stage.renderer.setPixelRatio(pixelRatio)
      Stage.renderer.setSize(cWidth / pixelRatio, cHeight / pixelRatio, false)
      Stage.composer.setPixelRatio(pixelRatio)
      Stage.composer.setSize(cWidth / pixelRatio, cHeight / pixelRatio)
      Stage.outlinePass.resolution = new THREE.Vector2(cWidth, cHeight)
      const cameraAspect: number = cWidth / cHeight
      Stage.camera.aspect = cameraAspect
      Stage.camera.updateProjectionMatrix()
      this.updateActiveCords()
    }
    this.renderScene()
  }

  private internalUpdate = () => {
    return new Promise(resolve => {
      const obj = Stage.outlinePass.selectedObjects.length
        ? (Stage.outlinePass.selectedObjects[0] as Section)
        : null

      if (obj) {
        const quiltData = obj.pIslands[0].quiltData

        this.setState({ quilt: quiltData }, () => {
          resolve(true)
        })
      } else {
        resolve(true)
      }
    })
  }

  private toggleSidebar = () => {
    this.setState(
      {
        ui: { ...this.state.ui, sidebar: !this.state.ui.sidebar },
      },
      () => this.resizeCanvas()
    )
  }

  public updateActiveCords = () => {
    if (this.state.selectPoint3D) {
      this.setState({ activePoint: null }, () => {
        const point3d = this.state.selectPoint3D! as THREE.Vector3
        const current_point: THREE.Vector3 = point3d.clone()
        current_point.project(Stage.camera)
        const {
          height: c_height,
          width: c_width,
          /* x: c_x,
           * y: c_y, */
        } = Stage.canvas.getBoundingClientRect()
        const x = (current_point.x / 2 + 0.5) * c_width
        const y = (-current_point.y / 2 + 0.5) * c_height
        this.setState({ activePoint: [x, y] })
      })
    } else {
      this.setState({ activePoint: null })
    }
  }

  public update = () => {
    if (this.renderDebounceTimer) clearTimeout(this.renderDebounceTimer)
    this.renderDebounceTimer = setTimeout(() => {
      if (this.state.mounted) {
        this.internalUpdate().then(() => {
          this.renderScene()
        })
      }
    }, 400)
  }

  handleSelect = () => {
    const { selection, selectionPoint } = this.state.mode.select()
    this.setState({ selectPoint3D: selectionPoint }, () => {
      Stage.outlinePass.selectedObjects = selection
      this.updateActiveCords()
      this.update()
    })
  }

  listnerMouseMove = (e: MouseEvent) => {
    Stage.mouseCords.x = e.clientX
    Stage.mouseCords.y = e.clientY
  }

  listnerOrbitStart = (e: any) => {
    Stage.camCords.x = e.target.object.position.x
    Stage.camCords.y = e.target.object.position.y
    Stage.camCords.z = e.target.object.position.z
    this.setState({ activePoint: null })
    Stage.outlinePass.enabled = false
  }

  listnerOrbitEnd = (e: any) => {
    Stage.outlinePass.enabled = true
    if (
      Stage.camCords.x === e.target.object.position.x &&
      Stage.camCords.y === e.target.object.position.y &&
      Stage.camCords.z === e.target.object.position.z
    ) {
      this.handleSelect()
    } else {
      this.updateActiveCords()
    }
    this.update()
  }

  render = () => {
    return (
      <div
        /* Stage Wrapper */
        className={styles.main_wrapper}
      >
        <React.Fragment /* Set Defaults */>
          <SetDefaultLights />
          {this.state.data && <ModelLoader data={this.state.data} />}
        </React.Fragment>
        <div
          /* Top Bar */
          className={styles.topbar_wrapper}
          // elevation={24}
          // square
        >
          <div className={styles.topBarBackgroundOverlay}></div>
          <IconButton className={styles.exit_button} onClick={this.props.reset}>
            <IoIosCloseCircle />
          </IconButton>
          <div /* Heading */ className={styles.configurator_heading}>
            Saddles Configurator
          </div>
          <div /* seperator */ className={styles.topbar_seperator}></div>
          <div /* Title */>
            {this.props.selectionTree.map((data, i) =>
              i !== 0 ? ` - ${data.value}` : data.value
            )}
          </div /* Title */>
          <div /* seperator */ className={styles.topbar_seperator}></div>
          <div /* spacer */ className={styles.spacer_horizontal} />
          <div /* seperator */ className={styles.topbar_seperator}></div>
          <Button disabled>Finalize</Button>
          <div /* seperator */ className={styles.topbar_seperator}></div>
          <SidebarToggler
            toggler={() => this.toggleSidebar()}
            active={this.state.ui.sidebar}
          />
        </div /* Top Bar */>
        <div
          /* Stage inner Wrapper 1 */
          className={styles.stage_inner_wrapper}
        >
          <div /* Mode Bar */ className={styles.modebar}></div /* Mode Bar */>
          <div
            /* Canvas Container */ className={styles.canvas_container}
            ref={this.container}
          >
            {this.state.selectPoint3D && <MaterialContainer />}
          </div>
          <Sidebar
            active={this.state.ui.sidebar}
            state={this.state}
            selectionTree={this.props.selectionTree}
          />
          {/* {this.state.activePoint && (
            <EditorPopup p={this.state.activePoint} mode={this.state.mode} />
          )} */}
          {/* {this.state.selectPoint3D && (
            <QuiltWrapper
              quiltCurrent={this.state.quilt}
              setQuilt={this.setQuilt}
              setQuiltColor={this.setQuiltColor}
              setQuiltSize={this.setQuiltSize}
              setQuiltRotation={this.setQuiltRotation}
            />
          )} */}
        </div /* Stage inner Wrapper */>
      </div /* Stage Wrapper */>
    )
  }

  public renderScene = () => {
    if (Stage.stats) {
      Stage.stats.begin()
    }
    Stage.composer.render()
    if (Stage.stats) {
      Stage.stats.end()
    }
  }

  public statsEnable() {
    Stage.stats = new Stats()
    document.body.appendChild(Stage.stats.dom)
  }

  public statsDisable() {
    if (Stage.stats) {
      document.body.removeChild(Stage.stats.dom)
      Stage.stats = undefined
    }
  }

  public statsToggle = (e: KeyboardEvent) => {
    if (e.key == 's') {
      if (Stage.stats) {
        this.statsDisable()
      } else {
        this.statsEnable()
      }
    }
  }

  public logger = (e: KeyboardEvent) => {
    if (e.key == 'l') {
      console.clear()
      console.log(Stage.camera)
      console.log(Stage.orbitCtrl)
    }
  }

  public static prepQuilt(q: Quilt) {
    if (!this._quiltUid.includes(q.uid)) {
      this._quilts.push(q)
      this._quiltUid.push(q.uid)
      return q
    } else {
      for (let quilt of this._quilts) {
        if (quilt.uid === q.uid) {
          return quilt
        }
      }
    }
  }

  public static get selection() {
    const selected_elements = Stage.outlinePass.selectedObjects
    const selected_islands = []
    for (const section of selected_elements) {
      const s = section as Section
      for (const island of s.pIslands) {
        selected_islands.push(island)
      }
    }
    return selected_islands
  }
}

export default Stage
